import * as React from 'react';
import { Autocomplete, Box, TextField } from '@mui/material';

interface IMultipleAutocompleteProps<T> {
    itemText: keyof T;
    itemValue: keyof T;
    label: string;
    helperText?: string;
    onChange?: (val: T[]) => void;
    value: T[];
    options: T[];
    disabled?: boolean;
    disableClearable?: boolean;
    // See DAutocomplete.minWidth — defaults to 25rem; pass a smaller value in
    // narrow flex/grid cells so the input shrinks instead of overflowing.
    minWidth?: string | number;
    // Optional: colour options that are already "used" elsewhere. Keyed by the
    // option's itemValue → usage count. When a count > 0 the option renders in
    // primary/bold (with ×N when > 1), so the dropdown itself shows who's taken
    // — no separate badge strip needed.
    usageCounts?: Map<string | number, number>;
    // Optional section header per option. MUI only emits a header when the
    // returned group CHANGES between adjacent options, so `options` must already
    // be sorted by the same key or a section repeats further down the list.
    groupBy?: (option: T) => string;
}

export function DMultipleAutocomplete<T>({
    itemValue,
    itemText,
    value,
    options,
    onChange,
    label,
    disabled = false,
    helperText,
    disableClearable = true,
    minWidth = '25rem',
    usageCounts,
    groupBy,
    ...rest
}: IMultipleAutocompleteProps<T>) {
    return (
        <Autocomplete
            {...rest}
            groupBy={groupBy}
            disabled={disabled}
            multiple={true}
            isOptionEqualToValue={(option, value: T) => {
                return option[itemValue] === value[itemValue];
            }}
            value={value}
            onChange={(event, val) => {
                if (onChange && Array.isArray(val)) {
                    onChange(val);
                }
            }}
            getOptionLabel={(val) => {
                if (!val) return '';
                if (typeof val === 'string' || typeof val === 'number')
                    return String(val);
                return String(val[itemText]);
            }}
            options={options}
            renderOption={
                usageCounts
                    ? (props, option) => {
                          // MUI puts `key` in props; pull it out so it isn't
                          // spread (React warns on a spread key).
                          const { key, ...optionProps } = props as {
                              key?: React.Key;
                          } & React.HTMLAttributes<HTMLLIElement>;
                          const idValue = option[
                              itemValue
                          ] as unknown as string | number;
                          const count = usageCounts.get(idValue) ?? 0;
                          return (
                              <Box
                                  component={'li'}
                                  key={key}
                                  {...optionProps}
                                  sx={{
                                      color:
                                          count > 0
                                              ? 'primary.main'
                                              : 'inherit',
                                      fontWeight: count > 0 ? 600 : 400,
                                  }}
                              >
                                  {String(option[itemText])}
                                  {count > 1 ? ` ×${count}` : ''}
                              </Box>
                          );
                      }
                    : undefined
            }
            disableClearable={disableClearable}
            renderInput={(params) => {
                return (
                    <TextField
                        {...params}
                        disabled={disabled}
                        sx={{ width: '100%', minWidth }}
                        label={label}
                        helperText={helperText || ''}
                        margin={'dense'}
                        size={'small'}
                    />
                );
            }}
        />
    );
}
